import { useEffect } from "react";
import { DOCS_URL } from "../productLinks";
export { DOCS_URL } from "../productLinks";

const LINK_SELECTOR = ".HelpDialog__btn";
const FEEDBACK_LABEL = "Feedback";
const TEAMS_LABEL = "Teams";
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

type IconName = "chat" | "people";

const ICON_SHAPES: Record<IconName, Array<[tag: string, attrs: Record<string, string>]>> = {
  chat: [["path", { d: "M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" }]],
  people: [
    ["circle", { cx: "9", cy: "8", r: "3" }],
    ["path", { d: "M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" }],
    ["circle", { cx: "17", cy: "9", r: "2.5" }],
    ["path", { d: "M17 14a4 4 0 0 1 4 4v2" }],
  ],
};

// Replaces the upstream GitHub / YouTube marks, which no longer match the destinations.
function createIcon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NAMESPACE, "svg");
  const attrs: Record<string, string> = {
    viewBox: "0 0 24 24",
    width: "20",
    height: "20",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "1.5",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "data-ms-help-icon": name,
  };
  Object.entries(attrs).forEach(([key, value]) => svg.setAttribute(key, value));
  ICON_SHAPES[name].forEach(([tag, shapeAttrs]) => {
    const shape = document.createElementNS(SVG_NAMESPACE, tag);
    Object.entries(shapeAttrs).forEach(([key, value]) => shape.setAttribute(key, value));
    svg.append(shape);
  });
  return svg;
}

export interface HelpDialogActions {
  /** Opens the in-app feedback dialog. */
  onFeedback: () => void;
  /** Teams channel URL; the Teams link is omitted when empty. */
  teamsUrl: string;
}

function isUpstreamIssueLink(anchor: HTMLAnchorElement): boolean {
  return /github\.com\/excalidraw\/excalidraw\/issues/.test(anchor.getAttribute("href") ?? "");
}

function isUpstreamDocsLink(anchor: HTMLAnchorElement): boolean {
  return /docs\.excalidraw\.com/.test(anchor.getAttribute("href") ?? "");
}

function setContent(anchor: HTMLAnchorElement, label: string, icon: IconName) {
  anchor.querySelector(".HelpDialog__link-icon")?.replaceChildren(createIcon(icon));
  const textNodes = Array.from(anchor.childNodes).filter((n) => n.nodeType === Node.TEXT_NODE);
  const [first, ...rest] = textNodes;
  if (first) first.textContent = label;
  else anchor.append(label);
  rest.forEach((n) => n.remove());
}

function markExternal(anchor: HTMLAnchorElement, href: string) {
  anchor.setAttribute("href", href);
  anchor.setAttribute("target", "_blank");
  anchor.setAttribute("rel", "noopener noreferrer");
  anchor.dataset.msHelpLink = "true";
}

function closeHelpDialog(anchor: HTMLElement) {
  anchor.closest(".Modal__content")?.querySelector<HTMLElement>(".Modal__close")?.click();
}

/**
 * Rewrite the link row of a Help dialog so only Excalidraw-MS links remain:
 * Documentation, Feedback (opens the in-app dialog) and Teams.
 * Idempotent: already-rewritten anchors are skipped.
 */
export function customizeHelpDialogLinks(root: ParentNode, actions: HelpDialogActions): void {
  root.querySelectorAll<HTMLAnchorElement>(LINK_SELECTOR).forEach((anchor) => {
    if (anchor.dataset.msHelpLink) return;
    if (isUpstreamDocsLink(anchor)) {
      markExternal(anchor, DOCS_URL);
    } else if (isUpstreamIssueLink(anchor)) {
      setContent(anchor, FEEDBACK_LABEL, "chat");
      anchor.setAttribute("href", "#");
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
      anchor.setAttribute("role", "button");
      anchor.dataset.msHelpLink = "true";
      anchor.addEventListener("click", (event) => {
        event.preventDefault();
        closeHelpDialog(anchor);
        actions.onFeedback();
      });
      anchor.addEventListener("keydown", (event) => {
        if (event.key !== " ") return;
        event.preventDefault();
        anchor.click();
      });
      if (actions.teamsUrl) {
        const teams = anchor.cloneNode(true) as HTMLAnchorElement;
        setContent(teams, TEAMS_LABEL, "people");
        markExternal(teams, actions.teamsUrl);
        teams.removeAttribute("role");
        anchor.after(teams);
      }
    } else {
      anchor.remove();
    }
  });
}

/** Keeps the native Help dialog free of OSS Excalidraw links. */
export function useHelpDialogLinks(actions: HelpDialogActions): void {
  const { onFeedback, teamsUrl } = actions;
  useEffect(() => {
    const apply = () => customizeHelpDialogLinks(document, { onFeedback, teamsUrl });
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    apply();
    return () => observer.disconnect();
  }, [onFeedback, teamsUrl]);
}
